/**
 * SEO link map — single source of truth for breadcrumbs AND contextual
 * "related pages" clusters shown at the bottom of every public page.
 *
 * Why both come from one file:
 *   1. Anchors use *descriptive* text pulled from actual page titles so the
 *      link graph tells Google what each destination is about (not "learn more").
 *   2. Every public, indexable page gets contextual *incoming* links from
 *      sibling pages in its cluster — not just a sitemap row. Each cluster
 *      hub lists every member; each member lists its hub + siblings.
 *   3. React Router `<Link>` renders a real `<a href>` so links are in the
 *      initial HTML and are crawlable in the prerendered output.
 */

// Public, indexable page catalog. `title` is what the <a> text uses by default
// when a page is referenced from another page's related-links section.
// `cluster` groups topically-adjacent pages so we can auto-generate hub blocks.
export const PAGES = {
  "/": {
    title: "Dividend Portfolio Planner — plan, backtest, track",
    short: "Home",
    description: "Build and backtest a dividend portfolio with 30+ years of real market data.",
    cluster: "home",
  },
  "/how-it-works": {
    title: "How it works — Dividend Portfolio Planner",
    short: "How it works",
    description: "A step-by-step tour of building, backtesting, and tracking a dividend portfolio.",
    cluster: "learn",
  },
  "/how-we-rank": {
    title: "How we rank and score dividend stocks",
    short: "Our ranking method",
    description: "Our transparent methodology for scoring dividend safety, yield, and growth.",
    cluster: "learn",
  },
  "/calculator": {
    title: "Dividend Calculator — forecast monthly income",
    short: "Dividend Calculator",
    description: "Project monthly and annual dividend income with live yields, DRIP, and DCA.",
    cluster: "tools",
  },
  "/portfolio/new": {
    title: "Build a dividend portfolio",
    short: "Portfolio Builder",
    description: "Pick tickers, tune weights, backtest 30+ years with DRIP + DCA — no sign-up needed.",
    cluster: "tools",
  },
  "/research": {
    title: "Dividend stock research — safety, yield, growth",
    short: "Ticker Research",
    description: "Deep-dive research on any dividend stock — safety score, DGR, payout, 10-year history.",
    cluster: "tools",
  },
  "/demo": {
    title: "Watch a 60-second DPP demo",
    short: "Live demo",
    description: "See DPP build, backtest, and track a sample portfolio in under a minute.",
    cluster: "learn",
  },
  "/pricing": {
    title: "Pricing — Free, Core, and Pro plans",
    short: "Pricing",
    description: "Compare Free, Core ($9.99/mo), and Pro ($14.99/mo) plans — all with 30-year backtests.",
    cluster: "info",
  },
  "/faq": {
    title: "Frequently asked questions",
    short: "FAQ",
    description: "Answers on backtests, Pro plan, SnapTrade brokerage sync, and dividend data.",
    cluster: "info",
  },
  "/market-recap": {
    title: "Dividend Market Recap — weekly digest",
    short: "Market Recap",
    description: "Weekly dividend market digest with ex-dividend highlights and macro callouts.",
    cluster: "info",
  },
  "/trust": {
    title: "Trust Center — privacy, security, policies",
    short: "Trust Center",
    description: "Privacy policy, terms, security practices, and data-handling commitments.",
    cluster: "info",
  },
  "/coming-soon": {
    title: "Product roadmap — what's shipping next",
    short: "Roadmap",
    description: "See what's live, what's shipping next, and vote on the features you want first.",
    cluster: "info",
  },
  "/download": {
    title: "Install the Dividend Portfolio Planner app",
    short: "Get the app",
    description: "Install the iOS, Android, or desktop PWA for push alerts and offline access.",
    cluster: "info",
  },
  "/affiliate": {
    title: "DPP Affiliate program — earn $1 per Pro signup",
    short: "Affiliate program",
    description: "Earn recurring commissions by referring investors to Dividend Portfolio Planner.",
    cluster: "info",
  },
  "/vs": {
    title: "DPP vs other dividend trackers — comparison hub",
    short: "Compare DPP",
    description: "DPP vs Snowball, Sharesight, Seeking Alpha, Track Your Dividends, and more.",
    cluster: "compare",
  },
  "/vs/field": {
    title: "The dividend-tracker field — pick the right one",
    short: "Full field comparison",
    description: "Compare every major dividend tracking app on features, pricing, and data sources.",
    cluster: "compare",
  },
  "/vs/snowball": {
    title: "DPP vs Snowball Analytics",
    short: "DPP vs Snowball",
    description: "How DPP compares to Snowball Analytics on backtesting, pricing, and AI.",
    cluster: "compare",
  },
  "/vs/sharesight": {
    title: "DPP vs Sharesight",
    short: "DPP vs Sharesight",
    description: "DPP vs Sharesight on dividend tracking, free-tier limits, and portfolio depth.",
    cluster: "compare",
  },
  "/vs/seeking-alpha": {
    title: "DPP vs Seeking Alpha",
    short: "DPP vs Seeking Alpha",
    description: "DPP vs Seeking Alpha for dividend research, backtests, and portfolio modeling.",
    cluster: "compare",
  },
  "/vs/track-your-dividends": {
    title: "DPP vs Track Your Dividends",
    short: "DPP vs Track Your Dividends",
    description: "DPP vs Track Your Dividends on free-tier limits, projections, and backtests.",
    cluster: "compare",
  },
  "/vs/dividend-tracker": {
    title: "DPP vs Dividend Tracker",
    short: "DPP vs Dividend Tracker",
    description: "DPP vs Dividend Tracker on holdings sync, dividend history, and backtest depth.",
    cluster: "compare",
  },
};

// Topic clusters — a short label + the hub route for the cluster. Pages
// reference their cluster via `PAGES[path].cluster`; the RelatedLinks block
// pulls siblings from the cluster so every page gets contextual incoming
// links from every other page in the cluster.
export const CLUSTERS = {
  home: { label: "Home", hub: "/" },
  learn: { label: "Learn & methodology", hub: "/how-it-works" },
  tools: { label: "Tools & calculators", hub: "/portfolio/new" },
  compare: { label: "Comparisons", hub: "/vs" },
  info: { label: "Pricing, policies & more", hub: "/pricing" },
};

/** Breadcrumb trail for a given path. Returns an array of `{label, href}`
 *  (hrefs omitted on the terminal crumb so it renders as plain text).
 *  Falls back to Home → <short title> when a path doesn't match a special case. */
export function breadcrumbsFor(path) {
  const canonical = (path || "/").replace(/\/$/, "") || "/";
  if (canonical === "/") return [];

  // Hand-crafted trails for routes with a non-trivial hierarchy. Everything
  // else falls back to Home → <this page>.
  const map = {
    "/vs": [
      { label: "Home", href: "/" },
      { label: "Compare DPP" },
    ],
    "/vs/field": [
      { label: "Home", href: "/" },
      { label: "Compare DPP", href: "/vs" },
      { label: "The dividend-tracker field" },
    ],
    "/portfolio/new": [
      { label: "Home", href: "/" },
      { label: "Build a dividend portfolio" },
    ],
    "/market-recap": [
      { label: "Home", href: "/" },
      { label: "Dividend Market Recap" },
    ],
  };
  if (map[canonical]) return map[canonical];

  // `/vs/:slug` → Home → Compare DPP → <competitor>
  if (canonical.startsWith("/vs/")) {
    const p = PAGES[canonical];
    return [
      { label: "Home", href: "/" },
      { label: "Compare DPP", href: "/vs" },
      { label: p?.short || "Comparison" },
    ];
  }

  // `/market-recap/:slug` → Home → Market Recap → <title>
  if (canonical.startsWith("/market-recap/")) {
    return [
      { label: "Home", href: "/" },
      { label: "Dividend Market Recap", href: "/market-recap" },
      { label: "Weekly digest" },
    ];
  }

  const p = PAGES[canonical];
  return [
    { label: "Home", href: "/" },
    { label: p?.short || canonical.replace(/^\//, "") },
  ];
}

/** Related pages for a given path. Returns up to `limit` siblings in the
 *  same cluster (excluding the current page) plus the cluster hub if the
 *  current page isn't itself the hub. Each entry is {href, label, description}
 *  with descriptive anchor text pulled from PAGES[href].title. */
export function relatedLinksFor(path, limit = 6) {
  const current = (path || "/").replace(/\/$/, "") || "/";
  const page = PAGES[current];
  if (!page) return [];
  const cluster = page.cluster;
  const siblings = Object.entries(PAGES)
    .filter(([href, p]) => p.cluster === cluster && href !== current)
    .map(([href, p]) => ({ href, label: p.title, description: p.description }));
  const out = [];
  // Lead with the cluster hub for discoverability (unless we're on it).
  const hub = CLUSTERS[cluster]?.hub;
  if (hub && hub !== current && PAGES[hub]) {
    out.push({ href: hub, label: PAGES[hub].title, description: PAGES[hub].description, isHub: true });
  }
  for (const s of siblings) {
    if (out.length >= limit) break;
    if (!out.find((x) => x.href === s.href)) out.push(s);
  }
  return out;
}
